import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  LayoutGrid,
  QrCode,
  Shield,
  Smartphone,
  UserRound,
  Zap,
} from 'lucide-react';
import HubHeader from '../../components/ui/HubHeader';
import { HubLandingBackdrop } from '../../components/hub/HubLandingPicture';
import Seo from '../../components/Seo';
import { ClientPortalExampleModal } from '../../components/agency/ClientPortalExampleModal';
import { OperationsDashboardExampleModal } from '../../components/agency/OperationsDashboardExampleModal';
import { AdminToolExampleModal } from '../../components/agency/AdminToolExampleModal';
import { NeeviiExampleModal } from '../../components/agency/NeeviiExampleModal';
import { JessicaExampleModal } from '../../components/agency/JessicaExampleModal';
import { AgencyDeliverablesPanel } from '../../components/agency/AgencyDeliverablesPanel';

const ACCENT_ORANGE = '#cc9600';

/** Calibri stack for all agency copy except the hero `h1` (hub-font-title). */
const agencyCopy = "font-[Calibri,'Calibri_Light','Segoe_UI',sans-serif]";

/** Darker grey body on white panel (was neutral-700). */
const whitePanelBody = 'text-[#363636]';

/** Cancels `main` horizontal padding so blocks align with the partner / thumbnail strip. */
const mainFullBleed =
  '-mx-5 w-[calc(100%+2.5rem)] max-w-none sm:-mx-8 sm:w-[calc(100%+4rem)] md:-mx-12 md:w-[calc(100%+6rem)]';

/**
 * Inset for hero / services / CTAs so copy aligns with partner column text (left)
 * and thumbnail column content (right). Horizontal values match panel padding.
 */
const heroAlignInset =
  'pl-8 pr-6 sm:pl-10 sm:pr-8 lg:pl-10 lg:pr-10 xl:pl-12 xl:pr-12';

/** Extra horizontal gutter so the hero / CTAs sit in from the viewport edges. */
const pageGutter = 'px-4 sm:px-5 md:px-6 lg:px-8 xl:px-10';

/** Partner strip shell — edge-to-edge (no outer pageGutter); borders at viewport left + seam. */
const partnerPanelShell =
  'flex flex-col justify-center border-y border-l border-r border-white/15 bg-black/50 py-8 shadow-xl backdrop-blur-sm sm:py-10 lg:col-span-1 lg:border-r-0 lg:py-10 xl:py-12';

/** Merged: pageGutter left + former partner pl — keeps “Why partner” text aligned with hero. */
const partnerContentInset =
  'pl-12 pr-6 sm:pl-[3.75rem] sm:pr-8 md:pl-16 md:pr-8 lg:pl-[4.5rem] lg:pr-10 xl:pl-[5.5rem] xl:pr-12';

/** White panel shell — full-bleed right; top flush to partner on mobile. */
const whitePanelShell =
  'border-b border-l border-r border-t-0 border-white/15 bg-white py-8 text-black shadow-xl sm:py-8 lg:col-span-2 lg:border-l-0 lg:border-t lg:border-r lg:border-white/15 lg:py-10 xl:py-12';

/**
 * Stacked (max-lg): gutter + white pl/pr (matches old inset).
 * Side-by-side (lg+): column starts at ⅓ vw — pl = gutter/3 + white inner pl.
 */
const whiteContentInset =
  'pl-12 pr-[2.5rem] sm:pl-[3.75rem] sm:pr-[3.25rem] md:pl-16 md:pr-14 lg:pl-[3.167rem] lg:pr-[4.5rem] xl:pl-[3.833rem] xl:pr-[5.5rem]';

/** Light grey for “Why partner” bullets (was white / white/90). */
const partnerBulletGrey = 'text-[#b8b8b8]';

/** Gmail compose — opens in a new tab for a stable “draft” experience in the browser. */
const AGENCY_MAIL_COMPOSE_NEW_TAB =
  'https://mail.google.com/mail/?view=cm&fs=1&to=lmatthews@neevii.com&su=Neevii%20agency%20%2F%20white-label%20enquiry';

/** Default mail client — for visitors who do not use Gmail in the browser. */
const AGENCY_MAIL_MAILTO =
  'mailto:lmatthews@neevii.com?subject=Neevii%20agency%20%2F%20white-label%20enquiry';

/** Jessica hero panel — white with subtle lavender lift at the bottom. */
const jessicaHeroPanelShell =
  'border-b border-l border-r border-t-0 border-white/15 py-8 text-black shadow-xl sm:py-10 lg:col-span-2 lg:min-h-[32rem] lg:border-l-0 lg:border-t lg:border-r lg:border-white/15 lg:py-12 xl:min-h-[36rem] xl:py-14';

const jessicaHeroGradient = 'linear-gradient(180deg, #ffffff 0%, #f0e2ff 100%)';

/** Inset for Jessica hero white column — matches examples strip rhythm. */
const jessicaHeroContentInset =
  'flex h-full min-h-[18rem] flex-col px-6 py-2 sm:px-10 sm:py-4 md:px-12 lg:px-14 lg:py-6 xl:px-16';

const HERO_PILLARS = [
  {
    key: 'user-centric',
    title: 'User-centric design',
    Icon: UserRound,
    description: 'Thoughtful experiences that drive adoption and clarity.',
  },
  {
    key: 'modular',
    title: 'Modular systems',
    Icon: LayoutGrid,
    description: 'Composable UI and workflow layers that scale with your product.',
  },
  {
    key: 'prototyping',
    title: 'Rapid prototyping',
    Icon: Zap,
    description: 'High-fidelity concepts to validate ideas and win stakeholder buy-in.',
  },
  {
    key: 'secure',
    title: 'Secure by design',
    Icon: Shield,
    description: 'Privacy, access control, and dependable handoffs built in from day one.',
  },
];

const JESSICA_HERO_THUMB = '/assets/images/Agency_page_Jessica_section.webp';

const WHY_PARTNER = [
  'Design + product thinking',
  'Flexible white-label collaboration',
  'Fast iteration & prototyping',
  'Polished UI systems',
  'Remote-first workflow',
];

const APPROACH_PARAGRAPHS = [
  'Neevii Partnerships is a design-led product and workflow studio focused on portals, dashboards, internal tools, and operational systems for modern businesses and agencies.',
  'With a background in visual design and product development, the focus is on creating software that feels polished, intuitive, and genuinely useful — balancing clean user experience with fast iteration and practical business workflows.',
  'Projects range from client collaboration portals and operational dashboards to custom admin systems and rapid product prototypes. Modern AI-assisted workflows are used to accelerate development and exploration while maintaining a strong focus on usability, clarity, and design quality.',
  'Based in the UK and working remotely with agencies, startups, and service businesses.',
];

const APPROACH_OPERATIONAL_LINE =
  'Available for remote-first agency collaborations, product design systems, rapid prototyping, and internal workflow tooling.';

const THUMB_CARDS = [
  {
    key: 'neevii',
    modal: 'neevii',
    title: 'Neevii — Operational Accounting Platform',
    src: '/assets/images/Agency_neeviibannerv004.webp',
    alt: 'Neevii Smart Accounting — desktop dashboard and mobile guidance UI',
    description:
      'UK small-business accounting with invoicing, expense tracking, and HMRC-ready Self Assessment workflows.',
  },
  {
    key: 'portal',
    modal: 'portal',
    title: 'VELLO — Creative Collaboration Portal',
    src: '/assets/images/Portal_v001_thumb.webp',
    alt: 'VELLO creative collaboration portal preview',
    description:
      'Branded client hub with project timelines, documents, and secure messaging.',
  },
  {
    key: 'dashboard',
    modal: 'dashboard',
    title: 'Second Brain — Agency Command Centre',
    src: '/assets/images/Dashboard_thumb.webp',
    alt: 'Second Brain agency command centre preview',
    description: 'Consolidated analytics and reporting for executive decision-making.',
  },
  {
    key: 'admin',
    modal: 'admin',
    title: 'PulseFit Admin — Wellness Operations Platform',
    src: '/assets/images/Admin_v001_thumb.webp',
    alt: 'PulseFit Admin wellness operations platform preview',
    description: 'Tailored backend system to manage complex workflows and data.',
  },
];

const AgencyPage = () => {
  const [portalExampleOpen, setPortalExampleOpen] = useState(false);
  const [dashboardExampleOpen, setDashboardExampleOpen] = useState(false);
  const [adminExampleOpen, setAdminExampleOpen] = useState(false);
  const [neeviiExampleOpen, setNeeviiExampleOpen] = useState(false);
  const [jessicaExampleOpen, setJessicaExampleOpen] = useState(false);

  const openExampleModal = (modal) => {
    if (modal === 'portal') setPortalExampleOpen(true);
    else if (modal === 'dashboard') setDashboardExampleOpen(true);
    else if (modal === 'admin') setAdminExampleOpen(true);
    else if (modal === 'neevii') setNeeviiExampleOpen(true);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <>
      <Seo
        title="Agency Partnerships | Neevii"
        description="Design-led portals, dashboards and digital products — polished UI systems, rapid prototyping, and interactive product concepts for agencies."
      />
      <div className="relative min-h-screen text-white">
        <div className="fixed inset-0 z-0 bg-zinc-900" aria-hidden="true">
          <HubLandingBackdrop />
        </div>
        <div className="fixed inset-0 z-[1] bg-black/25" aria-hidden="true" />

        <div className="relative z-10 flex min-h-screen flex-col">
          <div className="sticky top-0 z-50 border-b border-white/10 bg-black/75 px-5 py-3 backdrop-blur-md sm:px-8 md:px-12 md:py-3">
            <HubHeader />
          </div>

          <main className={`flex-1 px-5 pb-16 pt-6 sm:px-8 md:px-12 md:pb-20 md:pt-10 ${agencyCopy}`}>
            {/* Hero — 1/3 partner panel + 2/3 Jessica showcase (matches examples strip) */}
            <div className={`relative ${mainFullBleed}`}>
              <section
                className="grid grid-cols-1 gap-0 lg:grid-cols-3"
                aria-labelledby="agency-hero-heading"
              >
                <div className={`${partnerPanelShell} lg:min-h-[32rem] xl:min-h-[36rem]`}>
                  <div className={`${partnerContentInset} flex h-full flex-col`}>
                    <header className="min-w-0 text-left">
                      <h1
                        id="agency-hero-heading"
                        className="hub-font-title text-[clamp(1.35rem,3.8vw,4.5rem)] font-light leading-[0.95] tracking-[-0.0125em] text-white"
                        style={{
                          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                        }}
                      >
                        <span className="block">Design-led portals, dashboards</span>
                        <span className="-mt-1 block sm:-mt-2" style={{ color: ACCENT_ORANGE }}>
                          &amp; digital products
                        </span>
                      </h1>
                      <p className="mt-5 max-w-xl text-base leading-relaxed text-[#a8a8a8] sm:mt-6 sm:text-lg sm:leading-relaxed lg:text-xl">
                        We design and build polished portals, dashboards and digital products that scale
                        with your business. Combining product thinking, refined UI systems and rapid
                        prototyping to deliver intuitive experiences – faster.
                      </p>
                    </header>

                    <ul
                      className="mt-10 grid grid-cols-1 gap-7 border-t border-white/10 pt-8 sm:grid-cols-2 sm:gap-6 lg:mt-auto lg:pt-10 xl:gap-8"
                      aria-label="Design principles"
                    >
                      {HERO_PILLARS.map(({ key, title, Icon, description }) => (
                        <li key={key} className="text-left">
                          <div className="mb-2 flex items-center gap-2.5" style={{ color: ACCENT_ORANGE }}>
                            <Icon className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" strokeWidth={1.5} aria-hidden />
                            <span className="text-sm font-semibold uppercase tracking-[0.1em] sm:text-[0.9375rem]">
                              {title}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed text-white/80 sm:text-[0.9375rem]">
                            {description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={jessicaHeroPanelShell} style={{ background: jessicaHeroGradient }}>
                  <div className={jessicaHeroContentInset}>
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3 sm:mb-6">
                      <div>
                        <p className="text-2xl font-semibold text-[#7c3aed] sm:text-3xl">Ouro</p>
                        <p className="text-base font-semibold text-neutral-700 sm:text-lg">AI assistant with memory you control</p>
                        <p className="mt-2 max-w-md text-xs leading-snug text-neutral-500">
                          Live AI demo — chat powered by Gemini Flash (UI prototype).
                        </p>
                      </div>
                      <Link
                        to="/ouro"
                        className="inline-flex items-center gap-2 rounded-full border border-[#c4b5fd] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#6d28d9] shadow-sm transition-colors hover:bg-[#f5f3ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:text-sm"
                      >
                        <QrCode className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Scan QR for mobile install</span>
                        <Smartphone className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                      </Link>
                    </div>

                    <div className="flex flex-1 items-center justify-center">
                      <button
                        type="button"
                        aria-label="Open Ouro interactive prototype"
                        className="group block w-full max-w-[min(100%,920px)] border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#cc9600]"
                        onClick={() => setJessicaExampleOpen(true)}
                      >
                        <img
                          src={JESSICA_HERO_THUMB}
                          alt="Ouro — conversational assistant and Creative Flow whiteboard on mobile"
                          width={1200}
                          height={720}
                          className="h-auto w-full object-contain transition-opacity group-hover:opacity-95"
                          loading="eager"
                          decoding="async"
                        />
                        <span className="mt-3 block text-center text-sm text-neutral-500 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                          Open interactive demo
                        </span>
                      </button>
                    </div>

                    <p className="mt-4 text-center text-sm text-neutral-600">
                      On your phone?{' '}
                      <Link
                        to="/ouro"
                        className="font-semibold text-[#6d28d9] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600]"
                      >
                        Scan QR to open the mobile app shell
                      </Link>
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Full-bleed 1/3 + 2/3 strip — backgrounds to viewport edges; content inset preserved */}
            <div className={`relative ${mainFullBleed} mt-12 sm:mt-16 lg:mt-20`}>
              <section
                className="grid grid-cols-1 gap-0 lg:grid-cols-3"
                aria-labelledby="agency-partner-heading"
              >
                <div className={partnerPanelShell}>
                  <div className={partnerContentInset}>
                    <h2
                      id="agency-partner-heading"
                      className="hub-font-title text-xl font-light uppercase tracking-[0.2em] sm:text-2xl md:text-3xl"
                      style={{ color: ACCENT_ORANGE }}
                    >
                      Why partner
                    </h2>
                    <ul className="mt-7 space-y-5 sm:mt-8">
                      {WHY_PARTNER.map((line) => (
                        <li key={line} className="flex gap-3.5 text-left sm:gap-4">
                          <CheckCircle2
                            className="mt-0.5 h-7 w-7 shrink-0 sm:h-8 sm:w-8"
                            style={{ color: ACCENT_ORANGE }}
                            strokeWidth={1.75}
                            aria-hidden
                          />
                          <span className={`text-lg leading-relaxed sm:text-xl ${partnerBulletGrey}`}>
                            {line}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={whitePanelShell}>
                  <div id="agency-examples" className={`scroll-mt-28 ${whiteContentInset}`}>
                    <p
                      className="mb-5 flex min-h-10 items-center border-b border-neutral-200/70 py-1.5 text-[0.9375rem] font-light leading-snug tracking-[0.02em] text-neutral-600 sm:mb-6 sm:min-h-11 sm:py-2 sm:text-[1rem] sm:text-neutral-700 md:mb-8"
                    >
                      Product systems for portals, dashboards, and internal operations
                    </p>
                    <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:gap-6 lg:gap-8">
                      {THUMB_CARDS.map(({ key, modal, title, src, alt, description }) => (
                        <article key={key} className="flex flex-col text-left">
                          <div className="mb-4 flex min-h-0 items-center justify-center bg-neutral-100">
                            {modal ? (
                              <button
                                type="button"
                                className="group block w-full max-w-[754px] border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600]"
                                onClick={() => openExampleModal(modal)}
                              >
                                <img
                                  src={src}
                                  alt={alt}
                                  width={754}
                                  height={451}
                                  className="h-auto w-full object-contain transition-opacity group-hover:opacity-95"
                                  loading="lazy"
                                  decoding="async"
                                />
                              </button>
                            ) : (
                              <img
                                src={src}
                                alt={alt}
                                width={754}
                                height={451}
                                className="h-auto w-full max-w-[754px] object-contain"
                                loading="lazy"
                                decoding="async"
                              />
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-black sm:text-lg">{title}</h3>
                          <p
                            className={`mt-3 text-base leading-relaxed sm:text-lg ${whitePanelBody}`}
                          >
                            {description}
                          </p>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className={`relative ${mainFullBleed} mt-12 sm:mt-16 lg:mt-20`}>
              <section
                className="grid grid-cols-1 gap-0 lg:grid-cols-3"
                aria-label="Approach and typical deliverables"
              >
                <div className={partnerPanelShell}>
                  <div className={partnerContentInset}>
                    <section aria-labelledby="agency-approach-heading">
                      <h2
                        id="agency-approach-heading"
                        className="hub-font-title text-xl font-light uppercase tracking-[0.2em] sm:text-2xl md:text-3xl"
                        style={{ color: ACCENT_ORANGE }}
                      >
                        Approach
                      </h2>
                      <div className="mt-7 space-y-6 sm:mt-8 sm:space-y-7">
                        {APPROACH_PARAGRAPHS.map((paragraph, index) => (
                          <p
                            key={index}
                            className="text-base leading-relaxed text-[#b8b8b8] sm:text-lg sm:leading-relaxed"
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      <p className="mt-8 max-w-none border-t border-white/10 pt-6 text-sm font-light leading-relaxed tracking-[0.02em] text-white/65 sm:mt-10 sm:pt-8 sm:text-base">
                        {APPROACH_OPERATIONAL_LINE}
                      </p>
                    </section>
                  </div>
                </div>

                <div className={whitePanelShell}>
                  <div className={whiteContentInset}>
                    <AgencyDeliverablesPanel embedded />
                  </div>
                </div>
              </section>
            </div>

            <div className={`relative ${mainFullBleed} mt-10 sm:mt-12 lg:mt-14`}>
              <div className={pageGutter}>
                <div className={heroAlignInset}>
                  <a
                    href={AGENCY_MAIL_COMPOSE_NEW_TAB}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center border border-[#cf8f1a] bg-[#cf8f1a] px-6 py-3 text-center text-base font-medium text-black transition-colors hover:bg-[#e0a322] sm:w-auto"
                  >
                    Discuss a project
                  </a>
                  <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-white/50">
                    Not using Gmail?{' '}
                    <a
                      href={AGENCY_MAIL_MAILTO}
                      className="text-white/80 underline decoration-white/35 underline-offset-[3px] transition-colors hover:text-white hover:decoration-white/55"
                    >
                      lmatthews@neevii.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </main>

          <footer className="relative z-10 border-t border-white/10 bg-black/40 px-6 py-10 backdrop-blur-sm">
            <div className="mx-auto max-w-6xl">
              <p className="hub-font-title text-sm text-white/80">
                <span className="text-[#ffbe33]">Neevii</span>
                <span className="text-white/60"> · London</span>
              </p>
              <p className="mt-4 text-xs text-white/50">
                © {new Date().getFullYear()} Neevii. All rights reserved.
              </p>
            </div>
          </footer>
        </div>
      </div>
      <ClientPortalExampleModal open={portalExampleOpen} onClose={() => setPortalExampleOpen(false)} />
      <OperationsDashboardExampleModal
        open={dashboardExampleOpen}
        onClose={() => setDashboardExampleOpen(false)}
      />
      <AdminToolExampleModal open={adminExampleOpen} onClose={() => setAdminExampleOpen(false)} />
      <NeeviiExampleModal open={neeviiExampleOpen} onClose={() => setNeeviiExampleOpen(false)} />
      <JessicaExampleModal open={jessicaExampleOpen} onClose={() => setJessicaExampleOpen(false)} />
    </>
  );
};

export default AgencyPage;
